import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { TaskView, getProgressTask, isEnded, subscribeProgress } from '@client/stores/progressStore'
import { fetchProgressTask, startProgressWatch } from '@client/stores/progressWatch'

//
// What a view needs to know about the job it started.
//
// Replaces useTask, which asked the server once a second for as long as a job
// ran. The state now comes from the store: a snapshot when the display opens
// and after every reconnect, socket events in between. The view holds no timer
// and no request of its own.
//

export type ProgressHandle = {
    task: TaskView | undefined
    /** Over, whichever way it went — a task nobody knows counts as over too. */
    finished: boolean
    failed: boolean
    /** Why it failed, when it did. */
    error: string
}

export default function useProgress(taskId: string): ProgressHandle {
    // The store listens for as long as at least one display is open.
    useEffect(() => startProgressWatch(), [])

    const readTask = useCallback(() => getProgressTask(taskId), [taskId])
    const task = useSyncExternalStore(subscribeProgress, readTask)

    // An id the store has never heard of. Waiting for it forever is what the
    // old mechanism did after a restart; better to ask once and give up.
    const [missing, setMissing] = useState(false)

    useEffect(() => {
        setMissing(false)
        if (!taskId) return

        let dropped = false
        fetchProgressTask(taskId).then((result) => {
            if (!dropped && result === 'missing') setMissing(true)
        })

        return () => {
            dropped = true
        }
    }, [taskId])

    // Only a real failure. A cancellation is something the user just asked for,
    // and telling them about it as an error would be odd.
    const failed = task?.state === 'failed'

    return {
        task,
        finished: taskId ? isEnded(task) || missing : false,
        failed,
        error: failed ? task?.error || '' : missing ? 'Unknown task.' : '',
    }
}
